// src/pages/solicitudes/CancelarSolicitudModal.js
import React, { useState, useEffect } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { toast } from 'react-toastify'
import api from 'api/api'

const MOTIVOS_CANCELACION = [
    { codigo: 'ubicacion_no_cubierta', descripcion: 'Ubicación fuera de area de cobertura' },
    { codigo: 'solicitud_duplicada', descripcion: 'Solicitud duplicada' },
    { codigo: 'contenido_inapropiado', descripcion: 'Contenido inapropiado o spam' },
    { codigo: 'solicitud_expirada', descripcion: 'Solicitud expirada sin actividad' },
    { codigo: 'solicitud_prueba', descripcion: 'Solicitud de prueba/test' },
    { codigo: 'otro', descripcion: 'Otro motivo (especificar)' }
]

function CancelarSolicitudModal({ show, onHide, solicitud, onCancelado }) {
    const [motivoCodigo, setMotivoCodigo] = useState('')
    const [motivoPersonalizado, setMotivoPersonalizado] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    // Limpiar estado al abrir/cerrar el modal
    useEffect(() => {
        if (show) {
            setMotivoCodigo('')
            setMotivoPersonalizado('')
            setError('')
        }
    }, [show])

    const handleCancelar = async () => {
        // Validaciones
        if (!motivoCodigo) {
            setError('Seleccionar un motivo de cancelación')
            return
        }

        if (motivoCodigo === 'otro' && !motivoPersonalizado.trim()) {
            setError('Especificar el motivo de cancelación')
            return
        }

        setError('')
        setLoading(true)

        try {
            const { datos, error: apiError } = await api.post(`a/solicitudes/${solicitud.id}/cancelar`, {
                motivoCodigo,
                motivoPersonalizado: motivoPersonalizado.trim()
            })

            if (apiError) {
                setError(apiError)
                return
            }

            toast.success('Solicitud cancelada correctamente')
            onCancelado && onCancelado(solicitud.id)
            onHide()
        } catch (err) {
            setError(err.message || 'Error al cancelar la solicitud')
        } finally {
            setLoading(false)
        }
    }

    if (!solicitud) return null

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Cancelar Solicitud #{solicitud.id}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="mb-3">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    Esta acción cancelará la solicitud y todas las negociaciones/propuestas activas asociadas.
                </Alert>

                <div className="mb-3">
                    <strong>Solicitud:</strong> {solicitud.descripcion || 'Sin descripción'}
                </div>
                <div className="mb-3">
                    <strong>Usuario:</strong> {solicitud.usuario?.nombre || 'N/A'}
                </div>
                <div className="mb-3">
                    <strong>Categoría:</strong> {solicitud.categoria?.nombre || 'N/A'}
                </div>

                <hr />

                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label><strong>Motivo de cancelación *</strong></Form.Label>
                        <Form.Select
                            value={motivoCodigo}
                            onChange={(e) => setMotivoCodigo(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Seleccionar motivo...</option>
                            {MOTIVOS_CANCELACION.map((motivo) => (
                                <option key={motivo.codigo} value={motivo.codigo}>
                                    {motivo.descripcion}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {motivoCodigo === 'otro' && (
                        <Form.Group className="mb-3">
                            <Form.Label><strong>Especificar el motivo *</strong></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={motivoPersonalizado}
                                onChange={(e) => setMotivoPersonalizado(e.target.value)}
                                placeholder="Describir el motivo de la cancelación..."
                                disabled={loading}
                                maxLength={500}
                            />
                            <Form.Text className="text-muted">
                                {motivoPersonalizado.length}/500 caracteres
                            </Form.Text>
                        </Form.Group>
                    )}

                    {motivoCodigo && motivoCodigo !== 'otro' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Comentario adicional (opcional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={motivoPersonalizado}
                                onChange={(e) => setMotivoPersonalizado(e.target.value)}
                                placeholder="Agregar detalles adicionales..."
                                disabled={loading}
                                maxLength={500}
                            />
                        </Form.Group>
                    )}

                    {error && (
                        <Alert variant="danger" className="mb-0">
                            {error}
                        </Alert>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Cerrar
                </Button>
                <Button variant="danger" onClick={handleCancelar} disabled={loading || !motivoCodigo}>
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Cancelando...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-times me-2"></i>
                            Confirmar Cancelación
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default CancelarSolicitudModal